import { DateTime } from 'luxon';
import * as buttons from 'smg/buttons';
import * as models from 'smg/customer-api/models';
import * as organizationInvites from 'smg/customer-api/v2/organization-invites';
import * as organizations from 'smg/customer-api/organizations';
import * as session from 'smg/customer-api/session';
import { IsValidEmail } from 'smg/email';
import * as errors from 'smg/errors';
import Modal from 'smg/modal';
import { Picture } from 'smg/picture';
import * as template from 'smg/template';
import { LogoMime } from 'smg/mime';
import * as toast from 'smg/toast';
import * as uploads from 'smg/uploads';
import { AccountReady } from '../account-session';
import { Relref } from '../hugo';

void main();

function apiCode(err: unknown): string {
    return errors.GetAxiosParamErrors(err)?.errors[0]?.code ?? '';
}

function rolePill(owner: boolean): HTMLSpanElement {
    const $pill = document.createElement('span');
    if (owner) {
        $pill.className = 'badge rounded-pill bg-primary-subtle text-primary-emphasis';
        $pill.textContent = 'Owner';
    } else {
        $pill.className = 'badge rounded-pill bg-secondary-subtle text-secondary-emphasis';
        $pill.textContent = 'Member';
    }
    return $pill;
}

function showInlineError($el: HTMLElement, message?: string) {
    if (message !== undefined) {
        $el.textContent = message;
    }
    $el.classList.remove('d-none');
}

function hideInlineError($el: HTMLElement) {
    $el.classList.add('d-none');
}

function inviteFailureMessage(err: unknown, email: string, orgName: string): string {
    const first = errors.GetAxiosParamErrors(err)?.errors[0];
    if (first?.code === 'ALREADY_MEMBER') {
        return `${email} is already a member of ${orgName}.`;
    }
    if (first?.param === 'email') {
        return 'Enter a valid email address.';
    }
    return errors.GetErrorMessage(err);
}

function confirmBody(text: string): HTMLParagraphElement {
    const $p = document.createElement('p');
    $p.textContent = text;
    return $p;
}

async function main() {
    if (await AccountReady) {
        return;
    }

    const user = (await session.Session()).user;

    const $createNameInput = document.querySelector('#organization-create-name') as HTMLInputElement;
    // The profile's organization field is the name most people will type here.
    $createNameInput.placeholder = user.company ?? '';

    const $invitations = document.querySelector('#organization-invitations') as HTMLElement;
    const $invitationsList = document.querySelector('#organization-invitations-list') as HTMLElement;
    const $loading = document.querySelector('#organization-loading') as HTMLElement;
    const $error = document.querySelector('#organization-error') as HTMLElement;
    const $retry = document.querySelector('#organization-retry') as HTMLButtonElement;
    const $intro = document.querySelector('#organizations-intro') as HTMLElement;
    const $list = document.querySelector('#organizations-list') as HTMLElement;
    const $toggleRow = document.querySelector('#organization-create-toggle-row') as HTMLElement;
    const $toggle = document.querySelector('#organization-create-toggle') as HTMLButtonElement;
    const $create = document.querySelector('#organization-create') as HTMLElement;
    const $explainer = document.querySelector('#organization-create-explainer') as HTMLElement;
    const $createForm = document.querySelector('#organization-create-form') as HTMLFormElement;
    const $createName = document.querySelector('#organization-create-name') as HTMLInputElement;
    const $createError = document.querySelector('#organization-create-error') as HTMLElement;
    const $createSubmit = $createForm.querySelector('[type="submit"]') as HTMLButtonElement;

    let justCreatedID = '';
    // Sending several addresses at once can half-succeed. The successes are
    // real (the list reloads to show them), so the failures are carried across
    // that reload and put back in the form with their errors.
    let failedInvites: { organizationID: string; addresses: string[]; messages: string[] } | undefined;

    $retry.addEventListener('click', () => {
        window.location.reload();
    });

    $toggle.addEventListener('click', () => {
        $explainer.classList.add('d-none');
        $create.classList.remove('d-none');
        $createName.focus();
    });

    $createForm.addEventListener('submit', e => {
        e.preventDefault();
        void submitCreate();
    });

    async function submitCreate() {
        const name = $createName.value.trim();
        if (!name) {
            showInlineError($createError);
            return;
        }
        hideInlineError($createError);
        buttons.Start($createSubmit);
        try {
            const created = await organizations.CreateOrganization({ name });
            justCreatedID = created.id;
            toast.Success(`${name} created.`);
            await load();
        } catch (err) {
            errors.ShowError(err, $createForm);
        } finally {
            buttons.Stop($createSubmit);
        }
    }

    function hidePage() {
        $invitations.classList.add('d-none');
        $loading.classList.add('d-none');
        $error.classList.add('d-none');
        $intro.classList.add('d-none');
        $list.classList.add('d-none');
        $toggleRow.classList.add('d-none');
        $create.classList.add('d-none');
    }

    async function load() {
        hidePage();
        $loading.classList.remove('d-none');
        try {
            const [list, invitations] = await Promise.all([
                organizations.Organizations(),
                organizationInvites.GetOrganizationInvites('accepted:false lapsed:false', 'id,expires,organization,alreadyMember,createdBy'),
            ]);
            $loading.classList.add('d-none');
            render(list, invitations);
        } catch {
            $loading.classList.add('d-none');
            $error.classList.remove('d-none');
        }
    }

    function render(list: models.Organization[], invitations: models.HyejooOrganizationInvite[]) {
        renderInvitations(invitations);
        $list.replaceChildren();
        if (list.length === 0) {
            $explainer.classList.remove('d-none');
            $create.classList.remove('d-none');
            return;
        }

        $list.classList.remove('d-none');
        $toggleRow.classList.remove('d-none');
        if (list.length > 1) {
            $intro.classList.remove('d-none');
        }

        for (let i = 0; i < list.length; i++) {
            if (i > 0) {
                const $hr = document.createElement('hr');
                $hr.className = 'my-5';
                $list.append($hr);
            }
            $list.append(renderSection(list[i]));
        }
    }

    function renderInvitations(invitations: models.HyejooOrganizationInvite[]) {
        $invitationsList.replaceChildren();
        let first = true;
        for (const item of invitations) {
            if (item.alreadyMember) {
                continue;
            }
            $invitationsList.append(renderInvitation(item, first));
            first = false;
        }
        if (!first) {
            $invitations.classList.remove('d-none');
        }
    }

    function renderInvitation(item: models.HyejooOrganizationInvite, first: boolean): HTMLElement {
        const $row = template.Clone('#organization-invitation-template').firstElementChild as HTMLElement;
        if (first) {
            $row.classList.remove('border-top');
        }

        const createdBy = item.createdBy;
        const inviterName = createdBy?.fullName().trim() || createdBy?.emailAddress.email || '';
        const $inviter = $row.querySelector('.organization-invitation-inviter') as HTMLElement;
        const $org = $row.querySelector('.organization-invitation-org') as HTMLElement;
        const $expires = $row.querySelector('.organization-invitation-expires') as HTMLElement;
        const $view = $row.querySelector('.organization-invitation-view') as HTMLAnchorElement;
        const $logo = $row.querySelector('.organization-invitation-logo') as HTMLImageElement;

        $inviter.textContent = inviterName;
        $org.textContent = item.organization?.name ?? '';
        $expires.textContent = `Expires ${item.expires.toLocaleString(DateTime.DATE_MED)}`;
        $view.href = `${Relref('/organization-invite')}?id=${encodeURIComponent(item.id)}`;

        if (item.organization?.logo) {
            $logo.classList.remove('d-none');
            void Picture($logo, item.organization.logo.url(), { displayWidth: 96, alt: '' });
        }

        return $row;
    }

    function renderSection(org: models.Organization): HTMLElement {
        const $section = template.Clone('#organization-section-template').firstElementChild as HTMLElement;
        const me = organizations.Membership(org, user.id);
        const owner = Boolean(me?.owner);
        const many = org.members.length > 10;
        const created = org.id === justCreatedID;
        if (created) {
            justCreatedID = '';
        }

        const $heading = $section.querySelector('.organization-heading') as HTMLElement;
        const $headingBody = $section.querySelector('.organization-heading-body') as HTMLElement;
        const $helper = $section.querySelector('.organization-helper') as HTMLElement;
        const $role = $section.querySelector('.organization-role') as HTMLElement;
        $role.append(rolePill(owner));

        if (created) {
            $heading.textContent = `You created ${org.name}`;
            $headingBody.textContent = "You're the Owner. Invite a colleague.";
            $headingBody.classList.remove('d-none');
        } else if (many) {
            $heading.textContent = `${org.members.length} members in ${org.name}`;
            if (owner) {
                $helper.textContent = `Only Owners can remove members. You can also rename ${org.name} and change the logo.`;
            } else {
                $helper.textContent = 'You can invite colleagues. Owners handle the name and logo. They also remove members.';
            }
            $helper.classList.remove('d-none');
        } else if (owner && org.logo) {
            $heading.textContent = `Your logo is on ${org.name}`;
            $headingBody.textContent = 'This mark appears next to the name wherever you switch into the organization view.';
            $headingBody.classList.remove('d-none');
        } else if (owner) {
            $heading.textContent = `You're an Owner of ${org.name}`;
            $helper.textContent = `Only Owners can remove members. You can also rename ${org.name} and change the logo.`;
            $helper.classList.remove('d-none');
        } else {
            $heading.textContent = `Your membership in ${org.name}`;
            $helper.textContent = 'You can invite colleagues. Owners handle the name and logo. They also remove members.';
            $helper.classList.remove('d-none');
        }

        const $logo = $section.querySelector('.organization-logo') as HTMLImageElement;
        const $ownerActions = $section.querySelector('.organization-owner-actions') as HTMLElement;
        const $logoAdd = $section.querySelector('.organization-logo-add') as HTMLButtonElement;
        const $logoChange = $section.querySelector('.organization-logo-change') as HTMLButtonElement;
        const $logoRemove = $section.querySelector('.organization-logo-remove') as HTMLButtonElement;
        const $logoInput = $section.querySelector('.organization-logo-input') as HTMLInputElement;
        const $logoHelper = $section.querySelector('.organization-logo-helper') as HTMLElement;
        const $logoError = $section.querySelector('.organization-logo-error') as HTMLElement;
        const $renameBtn = $section.querySelector('.organization-rename-btn') as HTMLButtonElement;
        const $renameForm = $section.querySelector('.organization-rename') as HTMLFormElement;
        const $renameName = $section.querySelector('.organization-rename-name') as HTMLInputElement;
        const $renameCancel = $section.querySelector('.organization-rename-cancel') as HTMLButtonElement;
        const $renameError = $section.querySelector('.organization-rename-error') as HTMLElement;
        const $renameSubmit = $renameForm.querySelector('[type="submit"]') as HTMLButtonElement;
        const $projectsLink = $section.querySelector('.organization-projects-link') as HTMLAnchorElement;
        const $projectsLinkText = $section.querySelector('.organization-projects-link-text') as HTMLElement;
        const $ordersLink = $section.querySelector('.organization-orders-link') as HTMLAnchorElement;
        const $ordersLinkText = $section.querySelector('.organization-orders-link-text') as HTMLElement;
        const $membersTitle = $section.querySelector('.organization-members-title') as HTMLElement;
        const $members = $section.querySelector('.organization-members') as HTMLElement;
        const $invitesTitle = $section.querySelector('.organization-invites-title') as HTMLElement;
        const $inviteOpen = $section.querySelector('.organization-invite-open') as HTMLButtonElement;
        const $inviteForm = $section.querySelector('.organization-invite-form') as HTMLFormElement;
        const $inviteFormTitle = $section.querySelector('.organization-invite-form-title') as HTMLElement;
        const $inviteClose = $section.querySelector('.organization-invite-close') as HTMLButtonElement;
        const $inviteEmails = $section.querySelector('.organization-invite-emails') as HTMLInputElement;
        const $inviteSubmit = $inviteForm.querySelector('[type="submit"]') as HTMLButtonElement;
        const $inviteErrors = $section.querySelector('.organization-invite-errors') as HTMLElement;
        const $invitesEmpty = $section.querySelector('.organization-invites-empty') as HTMLElement;
        const $invites = $section.querySelector('.organization-invites') as HTMLElement;
        const $leave = $section.querySelector('.organization-leave') as HTMLButtonElement;
        const $leaveError = $section.querySelector('.organization-leave-error') as HTMLElement;

        $projectsLink.href = `${Relref('/projects')}?organization=${encodeURIComponent(org.id)}`;
        $projectsLinkText.textContent = `${org.name} projects`;
        $ordersLink.href = `${Relref('/orders')}?organization=${encodeURIComponent(org.id)}`;
        $ordersLinkText.textContent = `${org.name} orders`;

        if (org.logo) {
            $logo.classList.remove('d-none');
            void Picture($logo, org.logo.url(), { displayWidth: 260, alt: `${org.name} logo` });
            const $picture = $logo.parentElement;
            if ($picture instanceof HTMLPictureElement) {
                $picture.classList.add('mb-2');
            }
        }

        if (owner) {
            $ownerActions.classList.remove('d-none');
            $logoHelper.classList.remove('d-none');
            if (org.logo) {
                $logoAdd.classList.add('d-none');
                $logoChange.classList.remove('d-none');
                $logoRemove.classList.remove('d-none');
            }
        }

        if (many) {
            $membersTitle.textContent = `Members (${org.members.length})`;
            $invitesTitle.textContent = `Pending invitations (${org.invites.length})`;
        }

        $inviteFormTitle.textContent = `Invite to ${org.name}`;
        if (failedInvites?.organizationID === org.id) {
            $inviteOpen.classList.add('d-none');
            $inviteForm.classList.remove('d-none');
            $inviteEmails.value = failedInvites.addresses.join(', ');
            for (const message of failedInvites.messages) {
                appendInviteError(message);
            }
            failedInvites = undefined;
        }

        for (let i = 0; i < org.members.length; i++) {
            $members.append(renderMember(org, org.members[i], owner, i === 0));
        }

        if (org.invites.length === 0) {
            $invitesEmpty.classList.remove('d-none');
        } else {
            $invitesEmpty.classList.add('d-none');
            for (let i = 0; i < org.invites.length; i++) {
                $invites.append(renderInvite(org, org.invites[i], i === 0));
            }
        }

        $renameBtn.addEventListener('click', () => {
            $renameName.value = org.name;
            hideInlineError($renameError);
            $renameForm.classList.remove('d-none');
            $renameBtn.classList.add('d-none');
            $renameName.focus();
        });

        $renameCancel.addEventListener('click', () => {
            $renameForm.classList.add('d-none');
            $renameBtn.classList.remove('d-none');
            hideInlineError($renameError);
        });

        $renameForm.addEventListener('submit', e => {
            e.preventDefault();
            void submitRename();
        });

        async function submitRename() {
            const name = $renameName.value.trim();
            if (!name) {
                showInlineError($renameError);
                return;
            }
            hideInlineError($renameError);
            buttons.Start($renameSubmit);
            try {
                await organizations.UpdateOrganization(org.id, { name });
                toast.Success('Name saved.');
                await load();
            } catch (err) {
                errors.ShowError(err, $renameForm);
            } finally {
                buttons.Stop($renameSubmit);
            }
        }

        let $logoClicked: HTMLButtonElement | undefined;
        $logoAdd.addEventListener('click', () => {
            $logoClicked = $logoAdd;
            $logoInput.click();
        });
        $logoChange.addEventListener('click', () => {
            $logoClicked = $logoChange;
            $logoInput.click();
        });

        $logoInput.addEventListener('change', () => {
            void submitLogo();
        });

        async function submitLogo() {
            const selected = $logoInput.files?.[0];
            if (!selected) {
                return;
            }
            hideInlineError($logoError);
            if (!LogoMime(selected.type)) {
                showInlineError($logoError);
                $logoInput.value = '';
                return;
            }
            if ($logoClicked) {
                buttons.Start($logoClicked);
            }
            try {
                const uploaded = await uploads.New([{ name: selected.name, mime: selected.type, data: selected }]);
                const file = uploaded[0];
                if (!file) {
                    throw new Error('Upload failed');
                }
                await organizations.UpdateOrganization(org.id, { name: org.name, uploadID: file.uploadID });
                toast.Success('Logo updated.');
                await load();
            } catch (err) {
                errors.ShowError(err, $section);
            } finally {
                $logoInput.value = '';
                if ($logoClicked) {
                    buttons.Stop($logoClicked);
                }
            }
        }

        $logoRemove.addEventListener('click', () => {
            new Modal({
                title: 'Remove this logo',
                body: confirmBody(`${org.name} will show without a logo. You can upload another later.`),
                confirmText: 'Remove logo',
                cancelText: 'Keep logo',
                onSubmit: async () => {
                    await organizations.UpdateOrganization(org.id, { name: org.name, uploadID: 0 });
                    toast.Success('Logo removed.');
                    await load();
                },
            });
        });

        $inviteOpen.addEventListener('click', () => {
            $inviteOpen.classList.add('d-none');
            $inviteForm.classList.remove('d-none');
            $inviteEmails.focus();
        });

        $inviteClose.addEventListener('click', () => {
            $inviteForm.classList.add('d-none');
            $inviteOpen.classList.remove('d-none');
            $inviteErrors.replaceChildren();
        });

        $inviteForm.addEventListener('submit', e => {
            e.preventDefault();
            void submitInvites();
        });

        async function submitInvites() {
            const seen = new Set<string>();
            const emails = $inviteEmails.value.split(',').
                map(part => part.trim()).
                filter(part => part !== '').
                filter(part => {
                    const key = part.toLowerCase();
                    if (seen.has(key)) {
                        return false;
                    }
                    seen.add(key);
                    return true;
                });
            $inviteErrors.replaceChildren();
            if (emails.length === 0) {
                appendInviteError('Enter an email address.');
                return;
            }
            buttons.Start($inviteSubmit);
            const succeeded: string[] = [];
            const failedAddresses: string[] = [];
            const failedMessages: string[] = [];
            try {
                for (const email of emails) {
                    if (!IsValidEmail(email)) {
                        failedAddresses.push(email);
                        failedMessages.push('Enter a valid email address.');
                        continue;
                    }
                    try {
                        await organizations.CreateOrganizationInvite(org.id, { email });
                        succeeded.push(email);
                    } catch (err) {
                        failedAddresses.push(email);
                        failedMessages.push(inviteFailureMessage(err, email, org.name));
                    }
                }
                if (succeeded.length === 0) {
                    for (const message of failedMessages) {
                        appendInviteError(message);
                    }
                    return;
                }
                if (succeeded.length === 1) {
                    toast.Success(`Invitation sent to ${succeeded[0]}.`);
                } else {
                    toast.Success(`Invitations sent to ${succeeded.length} colleagues.`);
                }
                if (failedAddresses.length > 0) {
                    failedInvites = { organizationID: org.id, addresses: failedAddresses, messages: failedMessages };
                }
                $inviteForm.classList.add('d-none');
                $inviteOpen.classList.remove('d-none');
                await load();
            } finally {
                buttons.Stop($inviteSubmit);
            }
        }

        function appendInviteError(message: string) {
            const $p = document.createElement('p');
            $p.className = 'text-danger small fw-bold mt-1 mb-0';
            $p.textContent = message;
            $inviteErrors.append($p);
        }

        $leave.addEventListener('click', () => {
            if (!me) {
                return;
            }
            hideInlineError($leaveError);
            const only = org.members.length === 1;
            let title: string;
            let body: string;
            if (only) {
                title = `Leave ${org.name} empty`;
                body = `You're the only member, so leaving empties ${org.name} and hides it from every account. Our sales team can send invitations into it later.`;
            } else {
                title = `Leave ${org.name}`;
                body = "Your account stays yours. You will stop seeing everyone else's projects and orders.";
            }
            new Modal({
                title,
                body: confirmBody(body),
                confirmText: 'Leave Organization',
                cancelText: 'Stay in organization',
                onSubmit: async () => {
                    try {
                        await organizations.DeleteOrganizationMember(org.id, me.id);
                    } catch (err) {
                        if (apiCode(err) === 'LAST_OWNER') {
                            showInlineError($leaveError, "You're the last Owner. Make someone else an Owner before you leave.");
                            return;
                        }
                        throw err;
                    }
                    toast.Success(`You left ${org.name}.`);
                    await load();
                },
            });
        });

        return $section;
    }

    function renderMember(org: models.Organization, member: models.OrganizationMember, ownerViewer: boolean, first: boolean): HTMLElement {
        const $row = template.Clone('#organization-member-template').firstElementChild as HTMLElement;
        if (first) {
            $row.classList.remove('border-top');
        }

        const name = organizations.MemberName(member);
        const $name = $row.querySelector('.organization-member-name') as HTMLElement;
        const $you = $row.querySelector('.organization-member-you') as HTMLElement;
        const $email = $row.querySelector('.organization-member-email') as HTMLElement;
        const $role = $row.querySelector('.organization-member-role') as HTMLElement;
        const $joinedDate = $row.querySelector('.organization-member-joined-date') as HTMLElement;
        const $promote = $row.querySelector('.organization-member-promote') as HTMLButtonElement;
        const $demote = $row.querySelector('.organization-member-demote') as HTMLButtonElement;
        const $remove = $row.querySelector('.organization-member-remove') as HTMLButtonElement;
        const $errorWrap = $row.querySelector('.organization-member-error') as HTMLElement;
        const $error = $errorWrap.querySelector('p') as HTMLElement;

        $name.textContent = name;
        $email.textContent = member.user?.email ?? '';
        $role.append(rolePill(Boolean(member.owner)));
        $joinedDate.textContent = member.created.toLocaleString(DateTime.DATE_MED);

        if (member.userID === user.id) {
            $you.classList.remove('d-none');
        }

        if (!ownerViewer) {
            $promote.remove();
            $demote.remove();
            $remove.remove();
            return $row;
        }

        if (member.owner) {
            $promote.classList.add('d-none');
        } else {
            $demote.classList.add('d-none');
        }
        if (member.userID === user.id) {
            $remove.classList.add('d-none');
        }

        $promote.addEventListener('click', () => {
            void setOwner(true);
        });
        $demote.addEventListener('click', () => {
            void setOwner(false);
        });

        async function setOwner(nextOwner: boolean) {
            const $btn = nextOwner ? $promote : $demote;
            buttons.Start($btn);
            try {
                await organizations.UpdateOrganizationMember(org.id, member.id, { owner: nextOwner });
                if (nextOwner) {
                    toast.Success(`${name} is now an Owner.`);
                } else {
                    toast.Success(`${name} is now a Member.`);
                }
                await load();
            } catch (err) {
                if (apiCode(err) === 'LAST_OWNER') {
                    $error.textContent = `${org.name} needs an Owner while it has members. Make someone else an Owner before you step down.`;
                    $errorWrap.classList.remove('d-none');
                    return;
                }
                errors.ShowError(err, $row);
            } finally {
                buttons.Stop($btn);
            }
        }

        $remove.addEventListener('click', () => {
            new Modal({
                title: `Remove ${name}`,
                body: confirmBody(`${name}'s account stays theirs, and they will stop seeing ${org.name}. Projects and orders they already created stay visible to the organization.`),
                confirmText: 'Remove member',
                cancelText: 'Keep member',
                onSubmit: async () => {
                    try {
                        await organizations.DeleteOrganizationMember(org.id, member.id);
                    } catch (err) {
                        if (apiCode(err) === 'LAST_OWNER') {
                            $error.textContent = `${name} is the last Owner. Make another member an Owner before you remove them.`;
                            $errorWrap.classList.remove('d-none');
                            return;
                        }
                        throw err;
                    }
                    toast.Success(`${name} was removed from ${org.name}.`);
                    await load();
                },
            });
        });

        return $row;
    }

    function renderInvite(org: models.Organization, invite: models.OrganizationInvite, first: boolean): HTMLElement {
        const $row = template.Clone('#organization-invite-template').firstElementChild as HTMLElement;
        if (first) {
            $row.classList.remove('border-top');
        }

        const $email = $row.querySelector('.organization-invite-email') as HTMLElement;
        const $by = $row.querySelector('.organization-invite-by') as HTMLElement;
        const $expires = $row.querySelector('.organization-invite-expires') as HTMLElement;
        const $resend = $row.querySelector('.organization-invite-resend') as HTMLButtonElement;
        const $revoke = $row.querySelector('.organization-invite-revoke') as HTMLButtonElement;

        $email.textContent = invite.email;
        $by.textContent = `Sent by ${organizations.AttributionName(org, invite.createdByUserID)}`;
        $expires.textContent = `Expires ${invite.expires.toLocaleString(DateTime.DATE_MED)}`;

        $resend.addEventListener('click', async () => {
            buttons.Start($resend);
            try {
                const resp = await organizations.CreateOrganizationInvite(org.id, { email: invite.email });
                toast.Success(`Invitation resent to ${invite.email}. Expires ${resp.expires.toLocaleString(DateTime.DATE_MED)}.`);
                await load();
            } catch (err) {
                errors.ShowError(err, $row);
            } finally {
                buttons.Stop($resend);
            }
        });

        $revoke.addEventListener('click', () => {
            new Modal({
                title: 'Revoke this invitation',
                body: confirmBody(`The link sent to ${invite.email} will stop working. Send a new invitation if you still want them to join.`),
                confirmText: 'Revoke',
                cancelText: 'Keep invitation',
                onSubmit: async () => {
                    await organizations.DeleteOrganizationInvite(org.id, invite.id);
                    toast.Success(`Invitation to ${invite.email} revoked.`);
                    await load();
                },
            });
        });

        return $row;
    }

    await load();
}
